import { Badge } from "@/components/Badge.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { InfoIcon, WTooltip } from "@/components/WTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { AddImpersonationForm } from "@/modules/access/delegation/AddImpersonationForm.tsx";
import {
    accessDelegationListQuery,
    deleteAccessDelegationMutation,
    deleteImpersonatorMutation,
    type AccessDelegationWithType,
} from "@/modules/access/delegation/accessDelegationApi.ts";
import { scopeLabel } from "@/modules/access/scopeLabel.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation, useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import type { AccessDelegationImpersonation } from "@warrenio/api-spec/spec.oats.gen";
import { useState } from "react";

export function DelegationView({ accessId }: { accessId: number }) {
    //#region Hooks
    const { t } = useLingui();
    const deleteMutation = useStandardMutation(deleteAccessDelegationMutation);

    const { data } = useStandardSuspenseQuery(accessDelegationListQuery);
    const obj = useDeletableResource(() => getResourceById(data, accessId, "access_delegation"), deleteMutation);
    //#endregion

    if (obj === undefined) {
        return <NavigateAfterDelete />;
    }

    const { $type, id, name, restricted, created_at, billing_account_id } = obj;

    async function onDelete() {
        await deleteMutation.mutateAsync({ id });
    }

    return (
        <>
            <Toolbar>
                <DeleteModal modalAction={onDelete}>
                    <Trans>
                        Deleting "{name}" will permanently remove access from all the users in this group to act as you.
                    </Trans>
                </DeleteModal>
            </Toolbar>

            <ContentPane className="VStack gap-4">
                <ViewTitle title={name} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableRow title={t`Access name`}>{name}</DetailsTableRow>
                        <DetailsTableRow title={t`Access type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Access scope`}>{scopeLabel(restricted)}</DetailsTableRow>
                    </DetailsTable>

                    <Spacer />

                    <DetailsTable>
                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>
                        <DetailsTableRow />
                        <DetailsTableBillingAccount isGlobal={!restricted} valueKey={billing_account_id} />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>Users who can access my account</Trans>} />
                <ImpersonationsTable obj={obj} />
            </ContentPane>
        </>
    );
}

function ImpersonationsTable({ obj }: { obj: AccessDelegationWithType }) {
    const { t } = useLingui();
    const deleteImpersonationMutation = useStandardMutation(deleteImpersonatorMutation);
    const [isVisible, setIsVisible] = useState(false);

    const { id, impersonations } = obj;

    async function onImpersonationDelete(impersonation: AccessDelegationImpersonation) {
        await deleteImpersonationMutation.mutateAsync({
            impersonation_uuid: impersonation.uuid,
            delegation_id: id,
        });
    }

    return (
        <WTable
            afterTable={
                !isVisible && (
                    <WButton
                        color="primary"
                        size="bar"
                        variant="border"
                        icon="jp-icon-add"
                        action={() => setIsVisible(!isVisible)}
                    >
                        <Trans>Add users</Trans>
                    </WButton>
                )
            }
        >
            <thead>
                <tr>
                    <th>
                        <Trans>Name</Trans>
                    </th>
                    <th>
                        <Trans>Status</Trans>
                    </th>
                    <th>
                        <Trans>Created</Trans>
                    </th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {impersonations.map((item) => (
                    <tr key={item.uuid}>
                        <td>{item.grantee_username}</td>
                        <td>
                            {item.is_accepted ? (
                                <Badge color="success">
                                    <Trans>Accepted</Trans>
                                </Badge>
                            ) : (
                                <WTooltip text={t`User must accept access invitation from their profile view`}>
                                    <Badge color="warning">
                                        <Trans>Pending</Trans>
                                    </Badge>
                                    <InfoIcon className="ml-2" />
                                </WTooltip>
                            )}
                        </td>
                        <td>
                            <LongDate date={item.created_at} />
                        </td>
                        <td className="text-right">
                            <DeleteModal inTable modalAction={async () => await onImpersonationDelete(item)}>
                                <Trans>
                                    Removing user will revoke the rights from "{item.grantee_username}" to act as you.
                                </Trans>
                            </DeleteModal>
                        </td>
                    </tr>
                ))}
            </WTableBody>

            {isVisible && (
                <tfoot>
                    <tr>
                        <td colSpan={4}>
                            <AddImpersonationForm obj={obj} onClose={() => setIsVisible(false)} />
                        </td>
                    </tr>
                </tfoot>
            )}
        </WTable>
    );
}
